// store/index.js
import { configureStore } from "@reduxjs/toolkit";
import plansReducer from "./slices/plans.slice";
import userReducer from "./slices/user.slice";
import { userApi } from "./apis/user.api";
import { authApi } from "./apis/auth.api";
import { paymentApi } from "./apis/payment.api";
import { plansApi } from "./apis/plans.api";
import { clientsApi } from "./apis/clients.api";
import { propertiesApi } from "./apis/properties.api";
import { emailTemplateApi } from "./apis/emailTemplate.api";
import { userEmailTemplateApi } from "./apis/userEmailTemplate.api";
import { favoritesApi } from "./apis/favorites.api";
import { notesApi } from "./apis/notes.api";
import { meetingsApi } from "./apis/meetings.api";
import { emailsApi } from "./apis/emails.api";
import { documentsApi } from "./apis/documents.api";
import { dripCampaignApi } from "./apis/dripCampaign.api";
import { affiliateApi } from "./apis/affiliate.api";
import { newsletterApi } from "./apis/newsletter.api";
import { newsletterCampaignApi } from "./apis/newsletterCampaign.api";
import { activityApi } from "./apis/activity.api";
import { rentcastApi } from "./apis/rentcast.api";
import { dashboardApi } from "./apis/dashboard.api";
import { analyticsApi } from "./apis/analytics.api";
import { transactionsApi } from "./apis/transactions.api";
import { dealsApi } from "./apis/deals.api";
import { leadAnalyticsApi } from "./apis/leadAnalytics.api";
import { communicationLogApi } from "./apis/communicationLog.api";
import { referralApi } from "./apis/referral.api";
import { teamApi } from "./apis/team.api";
import { dealAnalysisApi } from "./apis/dealAnalysis.api";
import { neighborhoodFarmApi } from "./apis/neighborhoodFarm.api";
import { soiContactApi } from "./apis/soiContact.api";
import { showingFeedbackApi } from "./apis/showingFeedback.api";
import { followUpSequenceApi } from "./apis/followUpSequence.api";

// Wholesale CRM APIs
import { wholesaleSellersApi } from "./apis/wholesaleSellers.api";
import { wholesaleBuyersApi } from "./apis/wholesaleBuyers.api";
import { wholesaleDealsApi } from "./apis/wholesaleDeals.api";
import { wholesaleD4DApi } from "./apis/wholesaleD4D.api";
import { wholesaleOfferCalcApi } from "./apis/wholesaleOfferCalc.api";

// Real Estate Agent Feature APIs
import { goalsApi } from "./apis/goals.api";
import { listingChecklistApi } from "./apis/listingChecklist.api";
import { buyerPreferencesApi } from "./apis/buyerPreferences.api";
import { showingsApi } from "./apis/showings.api";
import { learningProgressApi } from "./apis/learningProgress.api";
import { closingCountdownApi } from "./apis/closingCountdown.api";
import { profitLossApi } from "./apis/profitLoss.api";
import { vaTaskApi } from "./apis/vaTasks.api";
import { investorPackageApi } from "./apis/investorPackages.api";
import { teamMemberApi } from "./apis/teamMembers.api";
import { agentKPIApi } from "./apis/agentKPI.api";

// Flipper CRM APIs
import { flipperProjectsApi } from "./apis/flipperProjects.api";
import { flipperBudgetsApi } from "./apis/flipperBudgets.api";
import { flipperContractorsApi } from "./apis/flipperContractors.api";

// Probate CRM APIs
import { probateCasesApi } from "./apis/probateCases.api";
import { probateChecklistsApi } from "./apis/probateChecklists.api";
import { probateCourtEventsApi } from "./apis/probateCourtEvents.api";
import { probateHeirsApi } from "./apis/probateHeirs.api";
import { probateSkipTraceApi } from "./apis/probateSkipTrace.api";

// Probate Extended APIs (Estate Assets, Valuations, Referrals, Attorney Notes)
import { probateEstateAssetsApi } from "./apis/probateEstateAssets.api";
import { probateValuationsApi } from "./apis/probateValuations.api";
import { probateReferralsApi } from "./apis/probateReferrals.api";
import { probateAttorneyNotesApi } from "./apis/probateAttorneyNotes.api";

// Flipper Extended APIs (Project Items, Lenders, Contractor Reviews)
import { flipperItemsApi } from "./apis/flipperItems.api";
import { estimateApi } from "./apis/estimate.api";
import { flipperLendersApi } from "./apis/flipperLenders.api";
import { flipperReviewsApi } from "./apis/flipperReviews.api";

// Vendor API
import { vendorApi } from "./apis/vendor.api";

// User Activity Event API (Admin Activity Tracking)
import { userActivityEventApi } from "./apis/userActivityEvent.api";

// Google Calendar API
import { googleCalendarApi } from "./apis/googleCalendar.api";
import { calendarEventsApi } from "./apis/calendarEvents.api";
import { propertyEnrichmentApi } from "./apis/propertyEnrichment.api";
import { socialMediaApi } from "./apis/socialMedia.api";
import { socialMediaAuthApi } from "./apis/socialMediaAuth.api";
import { bulkImportApi } from "./apis/bulkImport.api";

// Lead Scoring & Speed-to-Lead APIs
import { leadScoringApi } from "./apis/leadScoring.api";
import { speedToLeadApi } from "./apis/speedToLead.api";
import { smartListsApi } from "./apis/smartLists.api";
import { bulkSendApi } from "./apis/bulkSend.api";

import { agentWebsiteApi } from "./apis/agentWebsite.api";
import { twoFactorApi } from "./apis/twoFactor.api";
import { permissionsApi } from "./apis/permissions.api";
import { auditLogApi } from "./apis/auditLog.api";
import { pushApi } from "./apis/push.api";
import { accountApi } from "./apis/account.api";
import { seatsApi } from "./apis/seats.api";

const middlewares = [
  userApi.middleware,
  authApi.middleware,
  paymentApi.middleware,
  plansApi.middleware,
  clientsApi.middleware,
  propertiesApi.middleware,
  emailTemplateApi.middleware,
  userEmailTemplateApi.middleware,
  favoritesApi.middleware,
  notesApi.middleware,
  meetingsApi.middleware,
  emailsApi.middleware,
  documentsApi.middleware,
  dripCampaignApi.middleware,
  affiliateApi.middleware,
  newsletterApi.middleware,
  newsletterCampaignApi.middleware,
  activityApi.middleware,
  rentcastApi.middleware,
  dashboardApi.middleware,
  analyticsApi.middleware,
  transactionsApi.middleware,
  dealsApi.middleware,
  leadAnalyticsApi.middleware,
  communicationLogApi.middleware,
  referralApi.middleware,
  teamApi.middleware,
  dealAnalysisApi.middleware,
  neighborhoodFarmApi.middleware,
  soiContactApi.middleware,
  showingFeedbackApi.middleware,
  followUpSequenceApi.middleware,
  // Wholesale CRM APIs
  wholesaleSellersApi.middleware,
  wholesaleBuyersApi.middleware,
  wholesaleDealsApi.middleware,
  wholesaleD4DApi.middleware,
  wholesaleOfferCalcApi.middleware,
  // Real Estate Agent Feature APIs
  goalsApi.middleware,
  listingChecklistApi.middleware,
  buyerPreferencesApi.middleware,
  showingsApi.middleware,
  learningProgressApi.middleware,
  closingCountdownApi.middleware,
  profitLossApi.middleware,
  vaTaskApi.middleware,
  investorPackageApi.middleware,
  teamMemberApi.middleware,
  agentKPIApi.middleware,
  // Flipper CRM APIs
  flipperProjectsApi.middleware,
  flipperBudgetsApi.middleware,
  flipperContractorsApi.middleware,
  flipperItemsApi.middleware,
  estimateApi.middleware,
  flipperLendersApi.middleware,
  flipperReviewsApi.middleware,
  // Probate CRM APIs
  probateCasesApi.middleware,
  probateChecklistsApi.middleware,
  probateCourtEventsApi.middleware,
  probateHeirsApi.middleware,
  probateSkipTraceApi.middleware,
  probateEstateAssetsApi.middleware,
  probateValuationsApi.middleware,
  probateReferralsApi.middleware,
  probateAttorneyNotesApi.middleware,
  // Vendor API
  vendorApi.middleware,
  // User Activity Event API
  userActivityEventApi.middleware,
  // Google Calendar API
  googleCalendarApi.middleware,
  calendarEventsApi.middleware,
  propertyEnrichmentApi.middleware,
  socialMediaApi.middleware,
  socialMediaAuthApi.middleware,
  bulkImportApi.middleware,
  // Lead Scoring & Speed-to-Lead
  leadScoringApi.middleware,
  speedToLeadApi.middleware,
  smartListsApi.middleware,
  bulkSendApi.middleware,
  agentWebsiteApi.middleware,
  twoFactorApi.middleware,
  permissionsApi.middleware,
  auditLogApi.middleware,
  pushApi.middleware,
  accountApi.middleware,
  seatsApi.middleware,
];

export const store = configureStore({
  reducer: {
    plans: plansReducer,
    users: userReducer,

    // API reducers
    [userApi.reducerPath]: userApi.reducer,
    [authApi.reducerPath]: authApi.reducer,
    [paymentApi.reducerPath]: paymentApi.reducer,
    [plansApi.reducerPath]: plansApi.reducer,
    [clientsApi.reducerPath]: clientsApi.reducer,
    [propertiesApi.reducerPath]: propertiesApi.reducer,
    [emailTemplateApi.reducerPath]: emailTemplateApi.reducer,
    [userEmailTemplateApi.reducerPath]: userEmailTemplateApi.reducer,
    [favoritesApi.reducerPath]: favoritesApi.reducer,
    [notesApi.reducerPath]: notesApi.reducer,
    [meetingsApi.reducerPath]: meetingsApi.reducer,
    [emailsApi.reducerPath]: emailsApi.reducer,
    [documentsApi.reducerPath]: documentsApi.reducer,
    [dripCampaignApi.reducerPath]: dripCampaignApi.reducer,
    [affiliateApi.reducerPath]: affiliateApi.reducer,
    [newsletterApi.reducerPath]: newsletterApi.reducer,
    [newsletterCampaignApi.reducerPath]: newsletterCampaignApi.reducer,
    [activityApi.reducerPath]: activityApi.reducer,
    [rentcastApi.reducerPath]: rentcastApi.reducer,
    [dashboardApi.reducerPath]: dashboardApi.reducer,
    [analyticsApi.reducerPath]: analyticsApi.reducer,
    [transactionsApi.reducerPath]: transactionsApi.reducer,
    [dealsApi.reducerPath]: dealsApi.reducer,
    [leadAnalyticsApi.reducerPath]: leadAnalyticsApi.reducer,
    [communicationLogApi.reducerPath]: communicationLogApi.reducer,
    [referralApi.reducerPath]: referralApi.reducer,
    [teamApi.reducerPath]: teamApi.reducer,
    [dealAnalysisApi.reducerPath]: dealAnalysisApi.reducer,
    [neighborhoodFarmApi.reducerPath]: neighborhoodFarmApi.reducer,
    [soiContactApi.reducerPath]: soiContactApi.reducer,
    [showingFeedbackApi.reducerPath]: showingFeedbackApi.reducer,
    [followUpSequenceApi.reducerPath]: followUpSequenceApi.reducer,

    // Wholesale CRM APIs
    [wholesaleSellersApi.reducerPath]: wholesaleSellersApi.reducer,
    [wholesaleBuyersApi.reducerPath]: wholesaleBuyersApi.reducer,
    [wholesaleDealsApi.reducerPath]: wholesaleDealsApi.reducer,
    [wholesaleD4DApi.reducerPath]: wholesaleD4DApi.reducer,
    [wholesaleOfferCalcApi.reducerPath]: wholesaleOfferCalcApi.reducer,

    // Real Estate Agent Feature APIs
    [goalsApi.reducerPath]: goalsApi.reducer,
    [listingChecklistApi.reducerPath]: listingChecklistApi.reducer,
    [buyerPreferencesApi.reducerPath]: buyerPreferencesApi.reducer,
    [showingsApi.reducerPath]: showingsApi.reducer,
    [learningProgressApi.reducerPath]: learningProgressApi.reducer,
    [closingCountdownApi.reducerPath]: closingCountdownApi.reducer,
    [profitLossApi.reducerPath]: profitLossApi.reducer,
    [vaTaskApi.reducerPath]: vaTaskApi.reducer,
    [investorPackageApi.reducerPath]: investorPackageApi.reducer,
    [teamMemberApi.reducerPath]: teamMemberApi.reducer,
    [agentKPIApi.reducerPath]: agentKPIApi.reducer,

    // Flipper CRM APIs
    [flipperProjectsApi.reducerPath]: flipperProjectsApi.reducer,
    [flipperBudgetsApi.reducerPath]: flipperBudgetsApi.reducer,
    [flipperContractorsApi.reducerPath]: flipperContractorsApi.reducer,
    [flipperItemsApi.reducerPath]: flipperItemsApi.reducer,
    [estimateApi.reducerPath]: estimateApi.reducer,
    [flipperLendersApi.reducerPath]: flipperLendersApi.reducer,
    [flipperReviewsApi.reducerPath]: flipperReviewsApi.reducer,

    // Probate CRM APIs
    [probateCasesApi.reducerPath]: probateCasesApi.reducer,
    [probateChecklistsApi.reducerPath]: probateChecklistsApi.reducer,
    [probateCourtEventsApi.reducerPath]: probateCourtEventsApi.reducer,
    [probateHeirsApi.reducerPath]: probateHeirsApi.reducer,
    [probateSkipTraceApi.reducerPath]: probateSkipTraceApi.reducer,
    [probateEstateAssetsApi.reducerPath]: probateEstateAssetsApi.reducer,
    [probateValuationsApi.reducerPath]: probateValuationsApi.reducer,
    [probateReferralsApi.reducerPath]: probateReferralsApi.reducer,
    [probateAttorneyNotesApi.reducerPath]: probateAttorneyNotesApi.reducer,

    // Vendor API
    [vendorApi.reducerPath]: vendorApi.reducer,

    // User Activity Event API
    [userActivityEventApi.reducerPath]: userActivityEventApi.reducer,

    // Google Calendar API
    [googleCalendarApi.reducerPath]: googleCalendarApi.reducer,
    [calendarEventsApi.reducerPath]: calendarEventsApi.reducer,
    [propertyEnrichmentApi.reducerPath]: propertyEnrichmentApi.reducer,
    [socialMediaApi.reducerPath]: socialMediaApi.reducer,
    [socialMediaAuthApi.reducerPath]: socialMediaAuthApi.reducer,
    [bulkImportApi.reducerPath]: bulkImportApi.reducer,

    // Lead Scoring & Speed-to-Lead
    [leadScoringApi.reducerPath]: leadScoringApi.reducer,
    [speedToLeadApi.reducerPath]: speedToLeadApi.reducer,
    [smartListsApi.reducerPath]: smartListsApi.reducer,
    [bulkSendApi.reducerPath]: bulkSendApi.reducer,
    [agentWebsiteApi.reducerPath]: agentWebsiteApi.reducer,
    [twoFactorApi.reducerPath]: twoFactorApi.reducer,
    [permissionsApi.reducerPath]: permissionsApi.reducer,
    [auditLogApi.reducerPath]: auditLogApi.reducer,
    [pushApi.reducerPath]: pushApi.reducer,
    [accountApi.reducerPath]: accountApi.reducer,
    [seatsApi.reducerPath]: seatsApi.reducer,

  },

  // Middlewares
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(middlewares),
});
